import * as AvatarPrimitive from '@radix-ui/react-avatar';
import * as React from 'react';
import { cn } from '@/lib/utils';

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn('relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full', className)}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('rounded-full h-full w-full object-cover', className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

// Color palette for avatar fallbacks
const avatarColors = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#eab308',
  '#84cc16',
  '#22c55e',
  '#10b981',
  '#14b8a6',
  '#06b6d4',
  '#0ea5e9',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#a855f7',
  '#d946ef',
  '#ec4899',
  '#f43f5e',
];

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, children, ...props }, ref) => {
  function getInitials() {
    if (typeof children === 'string') {
      const splittedName = children.split(' ');
      const firstName = splittedName[0];
      const lastName = splittedName.at(-1);

      let initials = '';
      if (firstName === lastName || !lastName) {
        initials = `${firstName[0]}`;
      } else {
        initials = `${firstName[0]}${lastName[0]}`;
      }

      return initials.toUpperCase();
    }
    return children;
  }

  const backgroundColor = React.useMemo(() => {
    if (typeof children === 'string') {
      const stringHash = Array.from(children).reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const colorIndex = stringHash % avatarColors.length;
      return avatarColors[colorIndex];
    }
    return avatarColors[Math.floor(Math.random() * avatarColors.length)];
  }, [children]);

  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn(
        'flex h-full w-full items-center justify-center rounded-full text-white text-sm',
        className,
      )}
      {...props}
      style={{
        ...props.style,
        backgroundColor,
      }}
    >
      {getInitials()}
    </AvatarPrimitive.Fallback>
  );
});
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarFallback, AvatarImage };
